import React, { useState, useEffect } from 'react';
import { X, Star, Trash2, User, Clock } from 'lucide-react';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Låg', color: 'text-blue-400' },
  { value: 'medium', label: 'Medium', color: 'text-yellow-400' },
  { value: 'high', label: 'Hög', color: 'text-pink-400' },
];

const STATUS_OPTIONS = [
  { value: 'inbox', label: 'Inbox' },
  { value: 'next_up', label: 'Next Up' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'done', label: 'Done' },
];

function formatDateTime(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('sv-SE', { 
    day: 'numeric', 
    month: 'short', 
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

export function TaskEditModal({ task, isOpen, onClose }) {
  const { updateTask, deleteTask, tags } = useWorkspace();
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    tag: '',
    priority: 'medium',
    status: 'inbox',
    is_starred: false,
    is_today_focus: false,
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (task) {
      setFormData({
        title: task.title || '',
        description: task.description || '',
        tag: task.tag || '',
        priority: task.priority || 'medium',
        status: task.status || 'inbox',
        is_starred: task.is_starred || false,
        is_today_focus: task.is_today_focus || false,
      });
    }
  }, [task]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!task || !formData.title.trim()) return;
    
    setLoading(true);
    try {
      await updateTask(task.id, formData);
      onClose();
    } catch (error) {
      console.error('Error updating task:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!task) return;
    if (!window.confirm('Är du säker på att du vill ta bort denna uppgift?')) return;
    
    setLoading(true);
    try {
      await deleteTask(task.id);
      onClose();
    } catch (error) {
      console.error('Error deleting task:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStar = () => {
    handleChange('is_starred', !formData.is_starred);
  };

  const handleToggleFocus = () => {
    handleChange('is_today_focus', !formData.is_today_focus);
  };

  if (!task) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-[#141416] border-white/10 text-zinc-50 max-w-lg" data-testid="task-edit-modal">
        <DialogHeader>
          <DialogTitle className="text-lg font-medium font-['Outfit'] flex items-center justify-between">
            <span>Redigera uppgift</span>
            <div className="flex items-center gap-2">
              <button
                onClick={handleToggleStar}
                className={`p-2 rounded-lg hover:bg-white/10 transition-colors ${
                  formData.is_starred ? 'text-yellow-400' : 'text-zinc-500'
                }`}
                data-testid="modal-star-btn"
              >
                <Star className="w-5 h-5" fill={formData.is_starred ? 'currentColor' : 'none'} />
              </button>
              <button
                onClick={handleDelete}
                className="p-2 rounded-lg hover:bg-red-500/10 text-zinc-500 hover:text-red-400 transition-colors"
                data-testid="modal-delete-btn"
              >
                <Trash2 className="w-5 h-5" />
              </button>
            </div>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 pt-4">
          {/* Title */}
          <div className="space-y-2">
            <Label className="text-zinc-400 text-sm">Titel</Label>
            <Input
              value={formData.title}
              onChange={(e) => handleChange('title', e.target.value)}
              placeholder="Uppgiftens titel"
              className="bg-[#09090B] border-white/10 text-zinc-100 focus:border-zinc-500"
              data-testid="modal-title-input"
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label className="text-zinc-400 text-sm">Beskrivning</Label>
            <Textarea
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder="Lägg till en beskrivning..."
              className="bg-[#09090B] border-white/10 text-zinc-100 focus:border-zinc-500 min-h-[100px] resize-none"
              data-testid="modal-description-input"
            />
          </div>

          {/* Tag and Priority Row */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-zinc-400 text-sm">Tagg</Label>
              <Select value={formData.tag || 'none'} onValueChange={(v) => handleChange('tag', v === 'none' ? '' : v)}>
                <SelectTrigger className="bg-[#09090B] border-white/10 text-zinc-100" data-testid="modal-tag-select">
                  <SelectValue placeholder="Välj tagg" />
                </SelectTrigger>
                <SelectContent className="bg-[#141416] border-white/10">
                  <SelectItem value="none" className="text-zinc-100 focus:bg-white/5">
                    Ingen tagg
                  </SelectItem>
                  {tags.map((tag) => (
                    <SelectItem key={tag.id || tag.name} value={tag.name} className="text-zinc-100 focus:bg-white/5">
                      {tag.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-zinc-400 text-sm">Prioritet</Label>
              <Select value={formData.priority} onValueChange={(v) => handleChange('priority', v)}>
                <SelectTrigger className="bg-[#09090B] border-white/10 text-zinc-100" data-testid="modal-priority-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#141416] border-white/10">
                  {PRIORITY_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value} className={`${opt.color} focus:bg-white/5`}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Status */}
          <div className="space-y-2">
            <Label className="text-zinc-400 text-sm">Status</Label>
            <Select value={formData.status} onValueChange={(v) => handleChange('status', v)}>
              <SelectTrigger className="bg-[#09090B] border-white/10 text-zinc-100" data-testid="modal-status-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#141416] border-white/10">
                {STATUS_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value} className="text-zinc-100 focus:bg-white/5">
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Today Focus Toggle */}
          <div className="flex items-center justify-between p-3 rounded-lg bg-[#09090B] border border-white/10">
            <div>
              <p className="text-sm text-zinc-100">Dagens fokus</p>
              <p className="text-xs text-zinc-500">Visa i fokus-sektionen</p>
            </div>
            <button
              onClick={handleToggleFocus}
              className={`w-10 h-6 rounded-full transition-colors ${
                formData.is_today_focus ? 'bg-emerald-500' : 'bg-zinc-700'
              }`}
              data-testid="modal-focus-toggle"
            >
              <div className={`w-4 h-4 rounded-full bg-white transform transition-transform mx-1 ${
                formData.is_today_focus ? 'translate-x-4' : 'translate-x-0'
              }`} />
            </button>
          </div>

          {/* Created By and Time */}
          <div className="flex items-center gap-4 text-xs text-zinc-500 pt-2">
            {task.created_by_name && (
              <div className="flex items-center gap-2">
                <User className="w-3 h-3" />
                <span>Skapad av {task.created_by_name}</span>
              </div>
            )}
            {task.created_at && (
              <div className="flex items-center gap-2">
                <Clock className="w-3 h-3" />
                <span>{formatDateTime(task.created_at)}</span>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <Button
              variant="outline"
              onClick={onClose}
              className="flex-1 border-white/10 text-zinc-400 hover:bg-white/5 hover:text-zinc-100"
              data-testid="modal-cancel-btn"
            >
              Avbryt
            </Button>
            <Button
              onClick={handleSave}
              disabled={loading || !formData.title.trim()}
              className="flex-1 bg-zinc-100 text-zinc-900 hover:bg-white"
              data-testid="modal-save-btn"
            >
              {loading ? 'Sparar...' : 'Spara'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
